/* How the student did on the lesson's opening questions.
 *
 * Scoped to one lesson. The hook is the rapid diagnostic (02-practice rule 2a),
 * and its result decides how much support the page shows — supports that help a
 * novice become extraneous and then harmful once the student has the concept
 * (00-core rule 9).
 *
 * It routes support, never content. Nothing that carries the concept is hidden,
 * and a collapsed section is always one click from open: missing guidance harms
 * a novice (d ~ 0.9) more than redundant guidance harms an expert (d ~ 0.2-0.7),
 * so the default is guidance with an exit, not the reverse.
 */

import { createContext, useContext, useState, useMemo, useCallback } from 'react';

const HookContext = createContext(null);

export function HookProvider({ children }) {
  const [score, setScore] = useState(null);   // { got, total } | null

  const report = useCallback((got, total) => setScore({ got, total }), []);
  const reset = useCallback(() => setScore(null), []);

  /* "Has the concept" is a demonstrated result, never a self-report. */
  const confident = !!score && score.total > 0 && score.got === score.total;

  const value = useMemo(() => ({ score, confident, report, reset }), [score, confident, report, reset]);
  return <HookContext.Provider value={value}>{children}</HookContext.Provider>;
}

export function useHookScore() {
  return useContext(HookContext) || { score: null, confident: false, report: () => {}, reset: () => {} };
}
